import { cn, humanizedDurationsMap, humanizeDuration } from "@/lib/utils";
import { Countdown } from "@/components/ui/countdown";
import { forwardRef } from "react";
import type { ForwardedRef } from "react";
import type { Match } from "@/lib/types/api";
import { Clock10Icon } from "lucide-react";
import { AvatarBorder } from "@/components/AvatarBorder";
import { UserName } from "@/components/UserName";
import { Link } from "@tanstack/react-router";
import { t } from "@/lib/i18n";

const MatchCard = forwardRef(
  (
    props: {
      match: Match;
      walletAddress?: string;
    },
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const { match, walletAddress } = props;

    // Calculate squad ratios
    const calculateSquadRatios = () => {
      const hostValue = match.players[0].squadDetails.squadValue;
      const challengerValue = match.players[1]?.squadDetails.squadValue;
      const isHostSuperior = hostValue >= challengerValue;
      const isTied = hostValue === challengerValue;

      return { isHostSuperior, isTied };
    };

    const { isHostSuperior, isTied } = calculateSquadRatios();

    // Determine if user won or lost
    const isWinner =
      walletAddress &&
      match.status === "completed" &&
      match.winner.squadOwner === walletAddress;
    const isLoser =
      walletAddress &&
      match.status === "completed" &&
      match.winner.squadOwner !== walletAddress;

    return (
      <div
        className={cn(
          `relative w-full match-card overflow-visible`,
          match.status === "completed" && walletAddress
            ? isWinner
              ? "history-bar-win"
              : "history-bar-lose"
            : "history-bar-win",
        )}
        // style={{
        //   backgroundImage:
        //   match.status === "completed" && walletAddress
        //   ? `url(/assets/${isWinner ? "win-history-bar.png" : "lose-history-bar.png"})`
        //   : "url(https://cdn.navalabs.io/assets/win-history-bar_TUsghf.png)",
        //   backgroundSize: "cover",
        //   backgroundPosition: "center",
        // }}
        ref={ref}
      >
        <Link
          to="/practice"
          className="absolute inset-0 z-0"
          aria-label={t("matchCard.viewMatchDetails")}
        />
        <div className="w-full flex justify-between items-center p-3">
          {/* User Profile */}
          <div
            onClick={(e) => e.stopPropagation()}
            className={cn(
              isTied ? "grayscale" : !isHostSuperior ? "grayscale" : "",
              "relative w-full max-w-[25%] flex flex-col items-center gap-1",
            )}
          >
            <AvatarBorder
              src={match.players[0].ownerProfile.profilePicture}
              decoration={match.players[0].ownerProfile.activeFrameUrl}
              size={44}
            />
            {!isTied && isHostSuperior && (
              <div
                className={cn(
                  "-mt-3 px-2 py-0.5 text-xs rounded-sm font-bold leading-none z-10",
                  "bg-emerald-400 text-emerald-950",
                )}
              >
                {match.status === "live" && "Leading"}
                {match.status === "completed" && "Winner"}
              </div>
            )}
            {isTied && (
              <div
                className={cn(
                  "-mt-3 px-2 py-0.5 text-xs rounded-sm font-bold leading-none z-10",
                  "bg-zinc-400 text-yellow-950",
                )}
              >
                {t("matchCard.tied")}
              </div>
            )}
            <UserName
              username={match.players[0]?.ownerProfile.username}
              className={cn(
                "w-full text-white text-sm font-semibold justify-center",
                isTied ? "text-zinc-400" : isHostSuperior && "text-emerald-500",
              )}
              isVerified={match.players[0]?.ownerProfile.isVerified}
              domainType={match.players[0]?.ownerProfile.domainType}
            />
          </div>
          <div className="w-fit flex flex-col justify-center items-center text-center">
            {(() => {
              const hostValue = match.players[0].squadDetails.squadValue;
              const challengerValue = match.players[1]?.squadDetails.squadValue;

              return (
                <div className="flex flex-col items-center gap-0.5">
                  {/* Status Badges */}
                  <StatusBadge status={match.status} />

                  {/* Host vs Challenger Value */}
                  <div className="flex gap-1 items-center mt-1 font-bold text-base text-zinc-400">
                    <div className="flex items-center">
                      <span
                        key={`host-${match.players[0].squadDetails.squadValue}`}
                        className={cn(
                          "value-blink",
                          isTied
                            ? "text-zinc-400"
                            : [
                                !isHostSuperior && "opacity-60",
                                hostValue > 0 && "text-emerald-400",
                                hostValue < 0 && "text-red-400",
                                hostValue === 0 && "text-white",
                              ],
                        )}
                      >
                        {match.players[0].squadDetails.squadValue.toFixed(2)}%
                      </span>
                    </div>
                    <span>:</span>
                    <div className="flex items-center">
                      <span
                        key={`challenger-${match.players[1]?.squadDetails.squadValue}`}
                        className={cn(
                          "value-blink",
                          isTied
                            ? "text-zinc-400"
                            : [
                                isHostSuperior && "opacity-60",
                                challengerValue > 0 && "text-emerald-400",
                                challengerValue < 0 && "text-red-400",
                                challengerValue === 0 && "text-white",
                              ],
                        )}
                      >
                        {match.players[1]?.squadDetails.squadValue.toFixed(2)}%
                      </span>
                    </div>
                  </div>

                  {/* Countdown */}
                  {match.status === "live" && (
                    <Countdown
                      className="text-sm font-semibold text-zinc-400 font-mono"
                      startTimestamp={match.endTimestamp}
                    />
                  )}

                  {/* Bet Amount */}
                  <div className="flex items-center text-xs font-semibold text-zinc-400 gap-1 mt-1">
                    <div className="flex items-center gap-1 text-xs font-semibold py-0.5 px-1 bg-white/10 rounded">
                      <Clock10Icon className="size-3" />
                      <span>{humanizeDuration(Number(match.duration))}</span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
          {/* User Profile */}

          <div
            onClick={(e) => e.stopPropagation()}
            className={cn(
              isTied ? "grayscale" : isHostSuperior ? "grayscale" : "",
              "relative w-full max-w-[25%] flex flex-col items-center gap-1",
            )}
          >
            <AvatarBorder
              src={match.players[1].ownerProfile.profilePicture}
              decoration={match.players[1].ownerProfile.activeFrameUrl}
              size={44}
            />
            {!isTied && !isHostSuperior && (
              <div
                className={cn(
                  "-mt-3 px-2 py-0.5 text-xs rounded-sm font-bold leading-none z-10",
                  "bg-emerald-400 text-emerald-950",
                )}
              >
                {match.status === "live" && t("matchCard.leading")}
                {match.status === "completed" && t("matchCard.winner")}
              </div>
            )}
            {isTied && (
              <div
                className={cn(
                  "-mt-3 px-2 py-0.5 text-xs rounded-sm font-bold leading-none z-10",
                  "bg-zinc-400 text-yellow-950",
                )}
              >
                {t("matchCard.tied")}
              </div>
            )}
            <UserName
              username={match.players[1]?.ownerProfile.username}
              className={cn(
                "w-full text-white text-sm font-semibold justify-center",
                isTied
                  ? "text-zinc-400"
                  : !isHostSuperior && "text-emerald-500",
              )}
              isVerified={match.players[1]?.ownerProfile.isVerified}
              domainType={match.players[1]?.ownerProfile.domainType}
            />
          </div>
        </div>

        {/* Floating Win/Loss Bar */}
        {match.status === "completed" && walletAddress && (
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2/3">
            <img
              src={
                isWinner
                  ? "https://cdn.navalabs.io/assets/win-status-bar_GUZYol.png"
                  : "https://cdn.navalabs.io/assets/lose-status-bar_lOgkqQ.png"
              }
              alt={isWinner ? t("matchCard.altTexts.won") : t("matchCard.altTexts.lost")}
              className="w-full h-auto"
            />
            <div className="absolute inset-0 flex items-center justify-between px-4 gap-2">
              <span className="text-white font-bold text-sm">
                {isWinner ? t("matchCard.won") : isLoser ? t("matchCard.lost") : t("matchCard.ended")}
              </span>
              {match.trophies && (
                <div className="flex items-center gap-1">
                  <img
                    src={
                      isWinner
                        ? "https://cdn.navalabs.io/icons/trophy-icon_zozppj.png"
                        : "https://cdn.navalabs.io/animated-trophy_gljtag.png"
                    }
                    alt={t("matchCard.altTexts.trophy")}
                    className="w-3 h-3"
                    style={{ imageRendering: "pixelated" }}
                  />
                  <span className="text-white font-bold text-sm">
                    {isWinner ? "+" : "-"}
                    {isWinner
                      ? match.trophies.trophiesWin
                      : match.trophies.trophiesLost}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  },
);

export const StatusBadge = ({ status }: { status: string }) => {
  if (status === "live") {
    return (
      <div className="flex items-center gap-1 text-yellow-400 animate-pulse">
        <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full" />
        <span className="text-xs font-semibold">{t("matchCard.liveMatch")}</span>
      </div>
    );
  }

  if (status === "waiting") {
    return (
      <div className="flex items-center gap-1 text-zinc-200 animate-pulse">
        <div className="w-1.5 h-1.5 bg-zinc-200 rounded-full" />
        <span className="text-xs font-semibold">{t("matchCard.waiting")}</span>
      </div>
    );
  }

  if (status === "completed") {
    return (
      <div className="flex items-center gap-1 text-zinc-200">
        <div className="w-1.5 h-1.5 bg-zinc-200 rounded-full" />
        <span className="text-xs font-semibold">{t("matchCard.matchEnded")}</span>
      </div>
    );
  }

  return <></>;
};

MatchCard.displayName = "MatchCard";

export default MatchCard;
